import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { useCreate, useDeleteMany, useTranslate } from 'react-admin';
import BorderBlockWithLabel from '@/components/common/border-block-with-label/border-block-with-label';
import Box from '@mui/material/Box';
import { Avatar } from '@/utils/types';
import { difference, filter, find, findIndex, intersection, map, union } from 'lodash';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import CustomList from '@/components/common/assign-list/custom-list-component';
import { MANAGER_PLACES, USERS } from '@/utils/CONST';
import { composeUri } from '@/utils/compose';
import { useParams } from 'react-router';
import useGetCurrentUser from '@/hooks/useGetCurrentUser';

interface Props {
    choices: Avatar[];
    chosen: Avatar[];
    chosenHasNextPage: boolean;
    chosenLoadMore: any;
    choiceHasNextPage: boolean;
    choiceIsFetching: boolean;
    chosenIsFetching: boolean;
    isLoading: boolean;
    choiceLoadMore: any;
    chosenIds: any[];
}

const AssignListComponent = ({
    choices,
    chosen,
    chosenHasNextPage,
    chosenLoadMore,
    choiceLoadMore,
    choiceHasNextPage,
    choiceIsFetching,
    chosenIsFetching,
    chosenIds,
    isLoading,
}: Props) => {
    const translate = useTranslate();
    const [checked, setChecked] = useState<Avatar[]>([]);
    const [left, setLeft] = useState(choices);
    const [right, setRight] = useState(chosen);
    const [deleteMany] = useDeleteMany();
    const [create] = useCreate();
    const { userId } = useParams();
    const { currentUser } = useGetCurrentUser();

    useEffect(() => {
        setLeft(choices);
    }, [choices]);

    useEffect(() => {
        setRight(chosen);
    }, [chosen]);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const handleCheckedRight = () => {
        map(leftChecked, ({ id }) => {
            create(`NEW_${MANAGER_PLACES}`, {
                data: {
                    placeOrEvent: id,
                    company: currentUser?.company,
                    userManager: composeUri(USERS, userId),
                },
                meta: { realResource: MANAGER_PLACES },
            });
        });

        setRight(right.concat(leftChecked));
        setLeft(difference(left, leftChecked));
        setChecked(difference(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        const ids = filter(chosenIds, ({ placeOrEvent }) =>
            find(rightChecked, ({ id: selectedID }) => selectedID === placeOrEvent),
        );

        deleteMany(`DEL_${MANAGER_PLACES}`, {
            ids: map(ids, ({ id }) => id),
        });

        setLeft(left.concat(rightChecked));
        setRight(difference(right, rightChecked));
        setChecked(difference(checked, rightChecked));
    };

    const handleToggle = (item: Avatar) => () => {
        const currentIndex = findIndex(checked, ({ id: search_id }) => search_id === item.id);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(item);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const numberOfChecked = (items: Avatar[]) => intersection(checked, items).length;

    const handleToggleAll = (items: Avatar[]) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(difference(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    return (
        <BorderBlockWithLabel p={2}>
            <Box
                display={'grid'}
                gap={2}
                justifyContent='center'
                alignItems='center'
                gridTemplateColumns={'1fr auto 1fr'}
            >
                <CustomList
                    checked={checked}
                    handleToggleAll={handleToggleAll}
                    handleToggle={handleToggle}
                    numberOfChecked={numberOfChecked}
                    title={translate('users.assign.choices')}
                    items={left}
                    loadMore={choiceLoadMore}
                    hasNext={choiceHasNextPage}
                    isFetching={choiceIsFetching}
                    isLoading={isLoading}
                />

                <Box display={'grid'}>
                    <Button
                        sx={{ my: 0.5 }}
                        variant='outlined'
                        size='small'
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label='move selected right'
                    >
                        <ArrowForward />
                    </Button>
                    <Button
                        sx={{ my: 0.5 }}
                        variant='outlined'
                        size='small'
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label='move selected left'
                    >
                        <ArrowBack />
                    </Button>
                </Box>

                <CustomList
                    checked={checked}
                    handleToggleAll={handleToggleAll}
                    handleToggle={handleToggle}
                    numberOfChecked={numberOfChecked}
                    title={translate('users.assign.chosen')}
                    items={right}
                    loadMore={chosenLoadMore}
                    hasNext={chosenHasNextPage}
                    isFetching={chosenIsFetching}
                    isLoading={isLoading}
                />
            </Box>
        </BorderBlockWithLabel>
    );
};

export default AssignListComponent;
